import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { StatusScreen } from '@/components/StatusScreen';
import { INTEGRATIONS_FUNCTION } from '@/lib/chatFunction';

export default function NetlifyCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    // If not authenticated, redirect to login preserving the current URL
    if (!user) {
      console.log('[NetlifyCallback] User not authenticated, redirecting to login');
      const returnUrl = location.pathname + location.search;
      navigate('/auth', {
        replace: true,
        state: { returnUrl }
      });
      return;
    }

    const handleCallback = async () => {
      // Debug: log full URL and all params
      console.log('[NetlifyCallback] Full URL:', window.location.href);
      console.log('[NetlifyCallback] Search params:', window.location.search);
      console.log('[NetlifyCallback] All params:', Object.fromEntries(searchParams.entries()));

      // Try to get params from URL first, then fall back to sessionStorage
      // (sessionStorage is set by inline script in index.html before SW can cause reloads)
      let code = searchParams.get('code');
      let state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      // Fallback to sessionStorage if URL params are missing
      if (!code || !state) {
        const savedPath = sessionStorage.getItem('oauth_callback_path');
        if (savedPath === '/auth/netlify/callback') {
          const savedCode = sessionStorage.getItem('oauth_callback_code');
          const savedState = sessionStorage.getItem('oauth_callback_state');
          if (savedCode && savedState) {
            console.log('[NetlifyCallback] Using preserved params from sessionStorage');
            code = savedCode;
            state = savedState;
          }
        }
      }

      // Clean up sessionStorage after reading
      sessionStorage.removeItem('oauth_callback_code');
      sessionStorage.removeItem('oauth_callback_state');
      sessionStorage.removeItem('oauth_callback_path');

      console.log('[NetlifyCallback] Final code:', code ? code.substring(0, 10) + '...' : null, 'state:', state, 'error:', errorParam);

      if (errorParam) {
        setError(`Erreur Netlify: ${searchParams.get('error_description') || errorParam}`);
        return;
      }

      if (!code || !state) {
        // More descriptive error if possible
        if (!code && !state) {
          setError('Paramètres OAuth manquants (code et state)');
        } else if (!code) {
          setError('Paramètre OAuth manquant (code)');
        } else {
          setError('Paramètre OAuth manquant (state)');
        }
        return;
      }

      try {
        // Ensure redirect URI matches exactly what was sent to Netlify
        // It must match the one in useIntegrations.ts
        const redirectUri = `${window.location.origin}/auth/netlify/callback`;
        console.log('[NetlifyCallback] Using redirectUri:', redirectUri);

        const { data, error } = await supabase.functions.invoke(INTEGRATIONS_FUNCTION, {
          body: {
            action: 'netlifyCallback',
            code,
            state,
            redirectUri,
          },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        // Check for saved context to redirect back to project
        const savedContext = sessionStorage.getItem('oauth_return_context');
        sessionStorage.removeItem('oauth_return_context');

        if (savedContext) {
          try {
            const context = JSON.parse(savedContext);
            if (context.projectId) {
              const url = `/project/${context.projectId}?netlify=connected${context.openDeploy ? '&openDeploy=true' : ''}`;
              console.log('[NetlifyCallback] Redirecting to project:', url);
              navigate(url, { replace: true });
              return;
            }
          } catch (e) {
            console.error('[NetlifyCallback] Failed to parse context:', e);
          }
        }

        // Fallback - redirect to home with success message
        navigate('/?netlify=connected', { replace: true });
      } catch (err) {
        console.error('Netlify callback error:', err);
        setError(err instanceof Error ? err.message : 'Erreur lors de la connexion');
      }
    };

    handleCallback();
  }, [searchParams, navigate, user, isLoading, location]);

  if (error) {
    return (
      <StatusScreen
        status="error"
        title="Erreur de connexion"
        meta={error}
        action={{ label: "Retour à l'accueil", onClick: () => navigate('/') }}
      />
    );
  }

  return <StatusScreen status="loading" title="Connexion à Netlify…" />;
}
